<div class="vmt-section">
  <h3 class="section-heading">Account Template</h3>
  <p class="section-desc">
    Provision and test the Azure Active Directory user for this class Account template.
    Forwarding is blocked until the AAD user is provisioned and confirmed.
  </p>

  <!-- No ACC template configured -->
  <div *ngIf="!accTemplateId" class="vmt-action-body vmt-center" style="padding: 40px 0;">
    <mat-icon class="vmt-state-icon" style="color: #9e9e9e;">account_box</mat-icon>
    <p class="vmt-state-title">No ACC template configured</p>
    <p class="vmt-state-sub">No Account (ACC) resource template is assigned to this class.</p>
  </div>

  <!-- 2-col grid (expands to 3-col when credentials card is visible) -->
  <div *ngIf="accTemplateId" class="vmt-grid" [class.vmt-grid-3col]="status === 'active' || status === 'tested'">

    <!-- ═══════════ LEFT / CARD 1: Template Info ═══════════ -->
    <mat-card class="vmt-card vmt-info-card">
      <mat-card-header>
        <mat-card-title class="vmt-card-title-row">
          <mat-icon class="vmt-card-title-icon">description</mat-icon>
          Template Details
        </mat-card-title>
        <mat-card-subtitle>Configured ACC resource template for this class</mat-card-subtitle>
      </mat-card-header>

      <mat-card-content class="vmt-info-content">
        <div class="vmt-info-grid">

          <div class="vmt-info-item vmt-info-full">
            <span class="vmt-info-label">
              <mat-icon class="vmt-meta-icon">label</mat-icon>Template Name
            </span>
            <span class="vmt-info-value">{{ templateName }}</span>
          </div>

          <div class="vmt-info-item vmt-info-full">
            <span class="vmt-info-label">
              <mat-icon class="vmt-meta-icon">fingerprint</mat-icon>Template ID
            </span>
            <span class="vmt-info-value vmt-mono">{{ accTemplateId }}</span>
          </div>

        </div>

        <!-- State chip -->
        <div class="vmt-state-chip-row">
          <span class="vmt-state-chip" [ngClass]="chipClass">
            <mat-icon class="chip-icon">{{ chipIcon }}</mat-icon>
            {{ statusLabel }}
          </span>
        </div>

        <p *ngIf="statusDetails" class="vmt-status-details">{{ statusDetails }}</p>
      </mat-card-content>
    </mat-card>

    <!-- ═══════════ MIDDLE / CARD 2: Credentials (active or tested) ═══════════ -->
    <mat-card *ngIf="status === 'active' || status === 'tested'" class="vmt-card vmt-info-card">
      <mat-card-header>
        <mat-card-title class="vmt-card-title-row">
          <mat-icon class="vmt-card-title-icon">vpn_key</mat-icon>
          Template Credentials
        </mat-card-title>
        <mat-card-subtitle>Login credentials for the ACC test account</mat-card-subtitle>
      </mat-card-header>

      <mat-card-content class="vmt-info-content">
        <app-ua-credentials-card [templateId]="accTemplateId!" [classId]="classId" [hideClose]="true">
        </app-ua-credentials-card>
      </mat-card-content>
    </mat-card>

    <!-- ═══════════ RIGHT / CARD 3: Provisioning Controls ═══════════ -->
    <mat-card class="vmt-card vmt-action-card">
      <mat-card-header>
        <mat-card-title class="vmt-card-title-row">
          <mat-icon class="vmt-card-title-icon">manage_accounts</mat-icon>
          Provisioning Controls
        </mat-card-title>
        <mat-card-subtitle>Provision and confirm the ACC template AAD user</mat-card-subtitle>
      </mat-card-header>

      <mat-card-content class="vmt-action-content">

        <!-- Action error banner -->
        <div *ngIf="actionError" class="vmt-error-banner">
          <mat-icon>error_outline</mat-icon>
          <span>{{ actionError }}</span>
        </div>

        <!-- Gate: no ACC subscription -->
        <div *ngIf="status === 'not_started' && !hasAccSubscriptions" class="vmt-action-body vmt-center">
          <mat-icon class="vmt-state-icon" style="color: #f57c00;">warning_amber</mat-icon>
          <p class="vmt-state-title">No ACC subscription configured</p>
          <p class="vmt-state-sub">
            Add at least one Azure subscription under <em>Account Subscriptions</em> before
            provisioning the AAD user.
          </p>
          <button mat-flat-button color="accent" (click)="goToAccSubscriptions.emit()">
            <mat-icon>manage_accounts</mat-icon>
            Go to Account Subscriptions
          </button>
        </div>

        <!-- NOT_STARTED (subscription present) -->
        <div *ngIf="status === 'not_started' && hasAccSubscriptions" class="vmt-action-body vmt-center">
          <mat-icon class="vmt-state-icon">cloud_queue</mat-icon>
          <p class="vmt-state-title">AAD user not provisioned</p>
          <p class="vmt-state-sub">
            Provision an Azure Active Directory user for this ACC template to begin testing.
          </p>
          <button mat-flat-button color="primary" [disabled]="!hasCloudSettings || isActioning"
            [matTooltip]="hasCloudSettings ? '' : 'Cloud-settings permission required'" (click)="provisionAadUser()">
            <mat-spinner *ngIf="isActioning" diameter="18" class="btn-spinner"></mat-spinner>
            <mat-icon *ngIf="!isActioning">person_add</mat-icon>
            {{ isActioning ? 'Starting…' : 'Provision AAD User' }}
          </button>
        </div>

        <!-- PROVISIONING -->
        <div *ngIf="status === 'provisioning'" class="vmt-action-body">
          <mat-progress-bar mode="indeterminate" class="vmt-progress-bar"></mat-progress-bar>
          <p class="vmt-state-title vmt-center-text">Provisioning AAD user…</p>
          <p class="vmt-state-sub vmt-center-text">
            Creating the Azure Active Directory user and resolving credentials.
            Checking every 5&nbsp;s&thinsp;—&thinsp;this usually completes within 2&nbsp;minutes.
          </p>
        </div>

        <!-- ACTIVE — user created; testing confirmation pending -->
        <div *ngIf="status === 'active'" class="vmt-action-body vmt-center">
          <mat-icon class="vmt-state-icon vmt-icon-success">check_circle</mat-icon>
          <p class="vmt-state-title">AAD user provisioned</p>
          <p class="vmt-state-sub">
            The template account is active. Review the credentials in the centre card, then
            confirm testing is complete.
          </p>
          <div class="vmt-btn-row">
            <button mat-stroked-button color="primary" [disabled]="isRegeneratingTap" (click)="openAzurePortal()"
              matTooltip="Open Azure Portal in a new tab">
              <mat-icon>open_in_new</mat-icon>
              Open Azure Portal
            </button>
            <button mat-stroked-button [disabled]="isRegeneratingTap" (click)="regenerateTap()"
              matTooltip="Generate a new Temporary Access Pass">
              <mat-spinner *ngIf="isRegeneratingTap" diameter="18" class="btn-spinner"></mat-spinner>
              <mat-icon *ngIf="!isRegeneratingTap">refresh</mat-icon>
              {{ isRegeneratingTap ? 'Regenerating…' : 'Regenerate TAP' }}
            </button>
          </div>
          <button mat-flat-button color="primary" style="margin-top: 8px;"
            [disabled]="!hasCloudSettings || isActioning || isRegeneratingTap"
            [matTooltip]="hasCloudSettings ? '' : 'Cloud-settings permission required'" (click)="confirmTested()">
            <mat-spinner *ngIf="isActioning" diameter="18" class="btn-spinner"></mat-spinner>
            <mat-icon *ngIf="!isActioning">task_alt</mat-icon>
            {{ isActioning ? 'Confirming…' : 'Testing Done' }}
          </button>
        </div>

        <!-- FAILED -->
        <div *ngIf="status === 'failed'" class="vmt-action-body vmt-center">
          <mat-icon class="vmt-state-icon vmt-icon-error">cancel</mat-icon>
          <p class="vmt-state-title">Provisioning failed</p>
          <p class="vmt-state-sub">An error occurred during AAD user provisioning.</p>
          <button mat-flat-button color="warn" [disabled]="!hasCloudSettings || isActioning"
            (click)="provisionAadUser()">
            <mat-spinner *ngIf="isActioning" diameter="18" class="btn-spinner"></mat-spinner>
            <mat-icon *ngIf="!isActioning">replay</mat-icon>
            {{ isActioning ? 'Retrying…' : 'Retry Provisioning' }}
          </button>
        </div>

        <!-- TESTED — terminal success -->
        <div *ngIf="status === 'tested'" class="vmt-action-body vmt-center">
          <mat-icon class="vmt-state-icon vmt-icon-confirmed">verified</mat-icon>
          <p class="vmt-state-title">Account template confirmed</p>
          <p class="vmt-state-sub">
            The AAD user has been validated. Auto-confirmation will trigger once all other templates
            pass testing.
          </p>
          <div class="vmt-btn-row" style="margin-top: 8px;">
            <button mat-stroked-button color="primary" [disabled]="isRegeneratingTap" (click)="openAzurePortal()"
              matTooltip="Open Azure Portal in a new tab">
              <mat-icon>open_in_new</mat-icon>
              Open Azure Portal
            </button>
            <button mat-stroked-button [disabled]="isRegeneratingTap" (click)="regenerateTap()"
              matTooltip="Generate a new Temporary Access Pass">
              <mat-spinner *ngIf="isRegeneratingTap" diameter="18" class="btn-spinner"></mat-spinner>
              <mat-icon *ngIf="!isRegeneratingTap">refresh</mat-icon>
              {{ isRegeneratingTap ? 'Regenerating…' : 'Regenerate TAP' }}
            </button>
          </div>
        </div>

      </mat-card-content>
    </mat-card>

  </div><!-- /vmt-grid -->

</div>
